import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/auth/auth.service';
import { namespacedStorageKey } from '../../../core/auth/storage-namespace';
import { MediaUploadApiService } from '../../../shared/data/media-upload-api.service';
import { NutritionistProfile, ProfileResponse, UpdateProfilePayload } from './profile-api.types';
import { profileMediaForCache } from '../../../shared/utils/media-storage.util';

@Injectable({ providedIn: 'root' })
export class ProfileApiService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly mediaApi = inject(MediaUploadApiService);

  getProfile(): Promise<NutritionistProfile> {
    return firstValueFrom(
      this.http.get<ProfileResponse>(`${environment.apiBaseUrl}/profile`),
    )
      .then((res) => {
        this.writeCache(res.profile);
        return res.profile;
      })
      .catch((err) => {
        const cached = this.readCache();
        if (this.isNetworkFailure(err) && cached) return cached;
        throw err;
      });
  }

  updateProfile(payload: UpdateProfilePayload): Promise<NutritionistProfile> {
    return firstValueFrom(
      this.http.patch<ProfileResponse>(`${environment.apiBaseUrl}/profile`, payload),
    ).then((res) => {
      this.writeCache(res.profile);
      return res.profile;
    });
  }

  uploadSlot(slot: 'logo' | 'photo' | 'signature', file: File): Promise<NutritionistProfile> {
    return this.mediaApi
      .upload<ProfileResponse>(`${environment.apiBaseUrl}/profile/media/${slot}/upload`, file, 'photo')
      .then((res) => {
        this.writeCache(res.profile);
        return res.profile;
      });
  }

  deleteSlot(slot: 'logo' | 'photo' | 'signature'): Promise<NutritionistProfile> {
    return this.mediaApi
      .delete<ProfileResponse>(`${environment.apiBaseUrl}/profile/media/${slot}`)
      .then((res) => {
        this.writeCache(res.profile);
        return res.profile;
      });
  }

  private cacheKey(): string {
    const scope = this.auth.user()?.uuid ?? 'anon';
    return `nutritionapp.profile.${scope}`;
  }

  private writeCache(profile: NutritionistProfile): void {
    localStorage.setItem(
      namespacedStorageKey(this.cacheKey()),
      JSON.stringify(profileMediaForCache(profile)),
    );
  }

  private readCache(): NutritionistProfile | null {
    try {
      const raw = localStorage.getItem(namespacedStorageKey(this.cacheKey()));
      if (!raw) return null;
      return JSON.parse(raw) as NutritionistProfile;
    } catch {
      return null;
    }
  }

  private isNetworkFailure(err: unknown): boolean {
    const status = (err as { status?: unknown } | null)?.status;
    return status === 0;
  }
}
